import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import AdminSidebar from '@/components/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Truck, Pencil, Trash2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminVehicles = () => {
  const { getAuthHeader } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  
  const [form, setForm] = useState({
    name: '',
    type: 'sedan',
    description: '',
    weekly_rate: 275,
    features: '',
    image_url: '',
    available: true
  });

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API_URL}/vehicles`, getAuthHeader());
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      type: 'sedan',
      description: '',
      weekly_rate: 275,
      features: '',
      image_url: '',
      available: true
    });
    setEditingVehicle(null);
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setForm({
      name: vehicle.name,
      type: vehicle.type,
      description: vehicle.description,
      weekly_rate: vehicle.weekly_rate,
      features: vehicle.features?.join(', ') || '',
      image_url: vehicle.image_url || '',
      available: vehicle.available
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.description) {
      toast.error('Please fill in required fields');
      return;
    }

    const payload = {
      ...form,
      features: form.features.split(',').map(f => f.trim()).filter(Boolean)
    };

    try {
      if (editingVehicle) {
        await axios.put(`${API_URL}/vehicles/${editingVehicle.id}`, payload, getAuthHeader());
        toast.success('Vehicle updated');
      } else {
        await axios.post(`${API_URL}/vehicles`, payload, getAuthHeader());
        toast.success('Vehicle added');
      }
      fetchVehicles();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Failed to save vehicle');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) return;
    try {
      await axios.delete(`${API_URL}/vehicles/${id}`, getAuthHeader());
      toast.success('Vehicle deleted');
      fetchVehicles();
    } catch (error) {
      toast.error('Failed to delete vehicle');
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-100" data-testid="admin-vehicles">
      <AdminSidebar />
      
      <main className="flex-1 p-6">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Vehicles</h1>
            <p className="text-slate-500">Manage your fleet inventory</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-vehicle-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Vehicle
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="vehicle-dialog">
              <DialogHeader>
                <DialogTitle>{editingVehicle ? 'Edit Vehicle' : 'Add New Vehicle'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Vehicle Name *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g., Toyota Camry 2024"
                    data-testid="vehicle-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={form.type} onValueChange={(value) => setForm({ ...form, type: value })}>
                      <SelectTrigger data-testid="vehicle-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sedan">Sedan</SelectItem>
                        <SelectItem value="suv">SUV</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Weekly Rate ($)</Label>
                    <Input
                      type="number"
                      value={form.weekly_rate}
                      onChange={(e) => setForm({ ...form, weekly_rate: parseFloat(e.target.value) })}
                      data-testid="vehicle-rate-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description *</Label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Vehicle description..."
                    rows={3}
                    data-testid="vehicle-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Features (comma separated)</Label>
                  <Input
                    value={form.features}
                    onChange={(e) => setForm({ ...form, features: e.target.value })}
                    placeholder="Bluetooth, Backup Camera, GPS..."
                    data-testid="vehicle-features-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Image URL</Label>
                  <Input
                    value={form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    placeholder="https://..."
                    data-testid="vehicle-image-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={form.available}
                    onCheckedChange={(checked) => setForm({ ...form, available: checked })}
                    data-testid="vehicle-available-switch"
                  />
                  <Label>Available for booking</Label>
                </div>
                <Button type="submit" className="w-full" data-testid="save-vehicle-btn">
                  {editingVehicle ? 'Update Vehicle' : 'Add Vehicle'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="bg-white">
          <CardContent className="p-0">
            {loading ? (
              <div className="py-8 text-center text-slate-500">Loading...</div>
            ) : vehicles.length === 0 ? (
              <div className="py-8 text-center text-slate-500">
                <Truck className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>No vehicles in fleet</p>
              </div>
            ) : (
              <Table className="table-modern">
                <TableHeader>
                  <TableRow>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Weekly Rate</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vehicles.map((vehicle) => (
                    <TableRow key={vehicle.id} data-testid={`vehicle-row-${vehicle.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <img 
                            src={vehicle.image_url || 'https://images.unsplash.com/photo-1606611013016-969c19ba27bb?w=100'} 
                            alt={vehicle.name}
                            className="w-16 h-12 object-cover rounded"
                          />
                          <span className="font-medium">{vehicle.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="capitalize">{vehicle.type}</TableCell>
                      <TableCell>${vehicle.weekly_rate}</TableCell>
                      <TableCell>
                        <Badge className={vehicle.available ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                          {vehicle.available ? 'Available' : 'Unavailable'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleEdit(vehicle)} data-testid={`edit-vehicle-${vehicle.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDelete(vehicle.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-vehicle-${vehicle.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminVehicles;
